import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * A route component that restricts access to authenticated users
 * and redirects unauthenticated users to the home page.
 * 
 * @param {object} props - Component props
 * @param {JSX.Element} props.children - Child components to render if authenticated
 * @param {string|string[]} [props.requiredRoles] - Optional role(s) required to access the route
 * @returns {JSX.Element} Protected route component
 */
const ProtectedRoute = ({ children, requiredRoles = null }) => {
  const { user, loading, isAuthenticated, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Store last authentication check time in session storage to reduce frequent checks
  const lastAuthCheckKey = 'lastAuthCheck';
  const authCheckInterval = 60000; // 1 minute

  const shouldCheckAuth = () => {
    const lastCheck = sessionStorage.getItem(lastAuthCheckKey);
    if (!lastCheck) return true;
    
    const now = Date.now();
    const timeSinceLastCheck = now - parseInt(lastCheck, 10);
    return timeSinceLastCheck > authCheckInterval;
  };

  useEffect(() => {
    // Only check authentication if we haven't checked recently
    if (!loading && !isAuthenticated() && shouldCheckAuth()) {
      sessionStorage.setItem(lastAuthCheckKey, Date.now().toString());
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Not logged in - redirect to home page
  if (!isAuthenticated()) {
    // Check if the user just logged out
    const justLoggedOut = sessionStorage.getItem('justLoggedOut') === 'true';
    
    // Clear the flag if it exists
    if (justLoggedOut) {
      sessionStorage.removeItem('justLoggedOut');
    }
    
    return <Navigate to="/" state={
      justLoggedOut ? undefined : { message: "Please log in to access this page" }
    } replace />;
  }

  // Check role requirements if specified
  if (requiredRoles) {
    // Convert single role to array for consistent handling
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    
    // Check if user has at least one of the required roles
    const hasRequiredRole = roles.some(role => hasRole(role));
    
    if (!hasRequiredRole) {
      return <Navigate to="/dashboard" state={{ message: "You don't have permission to access this page" }} replace />;
    }
  }
  
  return children;
};

export default ProtectedRoute; 